import type { TriageChoiceOption, TriageQuestion, TriageQuestionnaire } from '@core/entities/Triage';
import {
  Extension,
  GuidanceResponseStatusEnum,
  Questionnaire,
  QuestionnaireItemTypeEnum,
  type GuidanceResponse,
  type QuestionnaireItem,
} from '@core/fhir-r4/fhir';

const renderingMarkdownExtensionUrl = 'http://hl7.org/fhir/StructureDefinition/rendering-markdown';
const shortTextExtensionUrl = 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-shortText';
const minimumValueExtensionUrl = 'http://hl7.org/fhir/StructureDefinition/minValue';
const maximumValueExtensionUrl = 'http://hl7.org/fhir/StructureDefinition/maxValue';
const questionnaireItemControlExtensionUrl = 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';
const answerExpressionExtensionUrl = 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-answerExpression';
const answerOptionHintExtensionUrl = 'urn:inav:triage:answer-option-hint';
const singleQuestionType = 'single';
const categoricalSingleChoiceQuestionType = 'categorical_single_choice';
const multipleChoiceQuestionType = 'multiple_choice';
const groupMultipleQuestionType = 'group_multiple';
const symptomsQuestionType = 'symptoms';
const listQuestionType = 'list';
const radioButtonItemControlCode = 'radio-button';
const dropDownItemControlCode = 'drop-down';
const checkBoxItemControlCode = 'check-box';
const autocompleteItemControlCode = 'autocomplete';
const lookupItemControlCode = 'lookup';
const fhirQueryExpressionLanguage = 'application/x-fhir-query';
const stepSequenceExtensionUrl = 'urn:inav:triage:workflow:step-sequence';
const journeyRevisionExtensionUrl = 'urn:inav:triage:workflow:journey-revision';
const questionnaireEventSequenceExtensionUrl = 'urn:inav:triage:workflow:questionnaire-event-sequence';

export class R4TriageQuestionnaireParser {
  public parse(guidanceResponse: GuidanceResponse): TriageQuestionnaire {
    if (guidanceResponse.status !== GuidanceResponseStatusEnum.DATA_REQUIRED && guidanceResponse.status !== GuidanceResponseStatusEnum.DATA_REQUESTED) {
      throw new Error(`The triage response did not contain a questionnaire step. Received status '${guidanceResponse.status ?? 'undefined'}'.`);
    }

    const questionnaire = this.getContainedQuestionnaire(guidanceResponse);
    const encounterId = this.getEncounterId(guidanceResponse);
    const questions = (questionnaire.item ?? []).map((item) => this.mapQuestion(item));

    return {
      id: questionnaire.id ?? 'triage-questionnaire',
      title: questionnaire.title ?? 'Triage questions',
      encounterId,
      workflowStepIdentity: this.getWorkflowStepIdentity(questionnaire),
      questions,
      source: questionnaire,
    };
  }

  private getWorkflowStepIdentity(questionnaire: Questionnaire): TriageQuestionnaire['workflowStepIdentity'] {
    const journeyRevision = this.getOptionalWorkflowInteger(questionnaire.extension, journeyRevisionExtensionUrl);
    const stepSequence = this.getOptionalWorkflowInteger(questionnaire.extension, stepSequenceExtensionUrl);
    const questionnaireEventSequence = this.getOptionalWorkflowInteger(questionnaire.extension, questionnaireEventSequenceExtensionUrl);

    if (!journeyRevision && !stepSequence && !questionnaireEventSequence) {
      return {
        journeyRevision: 1,
        stepSequence: 1,
        questionnaireEventSequence: 1,
      };
    }

    if (!journeyRevision || !stepSequence || !questionnaireEventSequence) {
      throw new Error('The triage questionnaire did not include a complete workflow identity.');
    }

    return {
      journeyRevision,
      stepSequence,
      questionnaireEventSequence,
    };
  }

  private getOptionalWorkflowInteger(extensions: readonly Extension[] | undefined, url: string): number | undefined {
    const extension = extensions?.find((candidate) => candidate.url === url);
    if (!extension) {
      return undefined;
    }

    const value = extension?.valueInteger ?? extension?.valueDecimal;

    if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
      throw new Error(`The triage questionnaire did not include a valid workflow identity extension '${url}'.`);
    }

    return value;
  }

  private getContainedQuestionnaire(guidanceResponse: GuidanceResponse): Questionnaire {
    const questionnaire = guidanceResponse.contained?.find((resource) => resource.resourceType === 'Questionnaire');

    if (!(questionnaire instanceof Questionnaire)) {
      throw new TypeError('The triage response did not include a questionnaire.');
    }

    return questionnaire;
  }

  private getEncounterId(guidanceResponse: GuidanceResponse): string {
    const encounterReference = guidanceResponse.encounter?.reference;
    if (encounterReference?.startsWith('#')) {
      return encounterReference.slice(1);
    }

    const containedEncounter = guidanceResponse.contained?.find((resource) => resource.resourceType === 'Encounter');
    if (containedEncounter?.id) {
      return containedEncounter.id;
    }

    throw new Error('The triage response did not include encounter context.');
  }

  private mapQuestion(item: QuestionnaireItem): TriageQuestion {
    if (!item.linkId || !item.type) {
      throw new Error('The triage questionnaire contains an invalid question definition.');
    }

    const itemControlCodes = this.getExtensionCodes(item.extension, questionnaireItemControlExtensionUrl);
    const metadata = this.mapMetadata(item, itemControlCodes);

    switch (item.type) {
      case QuestionnaireItemTypeEnum.GROUP:
        return {
          linkId: item.linkId,
          text: item.text ?? item.linkId,
          type: this.mapGroupType(metadata?.questionType, itemControlCodes),
          required: item.required ?? false,
          children: this.mapChildren(item),
          ...(metadata ? { metadata } : {}),
        };
      case QuestionnaireItemTypeEnum.CHOICE:
        return {
          linkId: item.linkId,
          text: item.text ?? item.linkId,
          type: this.mapChoiceType(item, metadata?.questionType, itemControlCodes, metadata?.sourceListUrl),
          required: item.required ?? false,
          options: this.mapChoiceOptions(item, metadata?.sourceListUrl),
          ...(item.repeats ? { allowsMultiple: true } : {}),
          ...(metadata ? { metadata } : {}),
        };
      case QuestionnaireItemTypeEnum.INTEGER:
      case QuestionnaireItemTypeEnum.DECIMAL:
        return {
          linkId: item.linkId,
          text: item.text ?? item.linkId,
          type: 'slider',
          required: item.required ?? false,
          numericFormat: item.type === QuestionnaireItemTypeEnum.DECIMAL ? 'decimal' : 'integer',
          ...(metadata ? { metadata } : {}),
        };
      case QuestionnaireItemTypeEnum.BOOLEAN:
        return {
          linkId: item.linkId,
          text: item.text ?? item.linkId,
          type: 'boolean',
          required: item.required ?? false,
          ...(metadata ? { metadata } : {}),
        };
      case QuestionnaireItemTypeEnum.STRING:
      case QuestionnaireItemTypeEnum.TEXT:
        return {
          linkId: item.linkId,
          text: item.text ?? item.linkId,
          type: 'free-text',
          required: item.required ?? false,
          ...(metadata ? { metadata } : {}),
        };
      case QuestionnaireItemTypeEnum.ATTACHMENT:
        return {
          linkId: item.linkId,
          text: item.text ?? item.linkId,
          type: 'image-upload',
          required: item.required ?? false,
          ...(metadata ? { metadata } : {}),
        };
      default:
        throw new Error(`Question type '${item.type}' is not supported by the canonical triage questionnaire parser.`);
    }
  }

  private mapChildren(item: QuestionnaireItem): readonly TriageQuestion[] {
    const children = (item.item ?? []).map((child) => this.mapQuestion(child));

    if (children.length === 0) {
      throw new Error(`Grouped question '${item.linkId}' does not define any child items.`);
    }

    return children;
  }

  private mapGroupType(questionType: string | undefined, itemControlCodes: readonly string[]): TriageQuestion['type'] {
    switch (questionType) {
      case undefined:
      case '':
        if (itemControlCodes.includes(checkBoxItemControlCode)) {
          return 'group-multiple';
        }

      case 'group_single':
        return 'group-single';
      case groupMultipleQuestionType:
        return 'group-multiple';
      default:
        throw new Error(`Question type '${questionType}' is not supported by the canonical triage questionnaire parser.`);
    }
  }

  private mapChoiceType(
    item: QuestionnaireItem,
    questionType: string | undefined,
    itemControlCodes: readonly string[],
    sourceListUrl: string | undefined,
  ): TriageQuestion['type'] {
    switch (questionType) {
      case undefined:
      case '':
        if (sourceListUrl) {
          return 'list';
        }

        if (item.repeats) {
          return 'multiple-choice';
        }

        if (itemControlCodes.includes(radioButtonItemControlCode)) {
          return 'single';
        }

        if (itemControlCodes.includes(dropDownItemControlCode)) {
          return 'categorical-single-choice';
        }

        return 'categorical-single-choice';
      case singleQuestionType:
        return 'single';
      case categoricalSingleChoiceQuestionType:
        return 'categorical-single-choice';
      case multipleChoiceQuestionType:
        return 'multiple-choice';
      case symptomsQuestionType:
        return 'symptoms';
      case listQuestionType:
        return 'list';
      default:
        throw new Error(`Question type '${questionType}' is not supported by the canonical triage questionnaire parser.`);
    }
  }

  private mapChoiceOptions(item: QuestionnaireItem, sourceListUrl: string | undefined): readonly TriageChoiceOption[] {
    const options = (item.answerOption ?? []).map((option) => {
      if (option.valueCoding?.code) {
        const hint = this.getExtensionString(option.extension, answerOptionHintExtensionUrl);
        return {
          code: option.valueCoding.code,
          label: option.valueCoding.display ?? option.valueCoding.code,
          system: option.valueCoding.system ?? undefined,
          ...(hint ? { hint } : {}),
        } satisfies TriageChoiceOption;
      }

      if (option.valueString) {
        const hint = this.getExtensionString(option.extension, answerOptionHintExtensionUrl);
        return {
          code: option.valueString,
          label: option.valueString,
          ...(hint ? { hint } : {}),
        } satisfies TriageChoiceOption;
      }

      if (option.valueInteger !== undefined) {
        const hint = this.getExtensionString(option.extension, answerOptionHintExtensionUrl);
        return {
          code: `${option.valueInteger}`,
          label: `${option.valueInteger}`,
          ...(hint ? { hint } : {}),
        } satisfies TriageChoiceOption;
      }

      throw new Error(`Choice question '${item.linkId}' contains an unsupported answer option.`);
    });

    if (options.length === 0) {
      if (sourceListUrl) {
        return [];
      }

      throw new Error(`Choice question '${item.linkId}' does not define any answer options.`);
    }

    return options;
  }

  private mapMetadata(item: QuestionnaireItem, itemControlCodes: readonly string[]): TriageQuestion['metadata'] | undefined {
    const sourceListUrl = this.getSourceListUrl(item.extension);
    const derivedQuestionType = this.getDerivedQuestionType(item, itemControlCodes, sourceListUrl);
    const metadata = {
      questionType: derivedQuestionType,
      answerType: this.getDerivedAnswerType(item, derivedQuestionType, sourceListUrl),
      explanatoryText: this.getExtensionString(item._text?.extension, renderingMarkdownExtensionUrl)
        ?? this.getExtensionString(item.extension, renderingMarkdownExtensionUrl),
      sourceListUrl,
      sliderTitle: this.getExtensionString(item.extension, shortTextExtensionUrl),
      minimumValue: this.getExtensionNumber(item.extension, minimumValueExtensionUrl),
      maximumValue: this.getExtensionNumber(item.extension, maximumValueExtensionUrl),
    };

    return Object.values(metadata).some((value) => value !== undefined) ? metadata : undefined;
  }

  private getSourceListUrl(extensions: readonly Extension[] | undefined): string | undefined {
    return this.getAnswerExpressionQuery(extensions);
  }

  private getDerivedQuestionType(
    item: QuestionnaireItem,
    itemControlCodes: readonly string[],
    sourceListUrl: string | undefined,
  ): string | undefined {
    switch (item.type) {
      case QuestionnaireItemTypeEnum.GROUP:
        return itemControlCodes.includes(checkBoxItemControlCode)
          ? groupMultipleQuestionType
          : 'group_single';
      case QuestionnaireItemTypeEnum.CHOICE:
        if (itemControlCodes.includes(lookupItemControlCode)) {
          return symptomsQuestionType;
        }

        if (itemControlCodes.includes(autocompleteItemControlCode)) {
          return listQuestionType;
        }

        if (sourceListUrl) {
          return itemControlCodes.includes(lookupItemControlCode)
            ? symptomsQuestionType
            : listQuestionType;
        }

        if (item.repeats || itemControlCodes.includes(checkBoxItemControlCode)) {
          return multipleChoiceQuestionType;
        }

        if (itemControlCodes.includes(radioButtonItemControlCode)) {
          return singleQuestionType;
        }

        return categoricalSingleChoiceQuestionType;
      case QuestionnaireItemTypeEnum.INTEGER:
      case QuestionnaireItemTypeEnum.DECIMAL:
        return 'slider';
      case QuestionnaireItemTypeEnum.BOOLEAN:
        return 'boolean';
      case QuestionnaireItemTypeEnum.STRING:
      case QuestionnaireItemTypeEnum.TEXT:
        return 'free_text';
      case QuestionnaireItemTypeEnum.ATTACHMENT:
        return 'image_upload';
      default:
        return undefined;
    }
  }

  private getDerivedAnswerType(
    item: QuestionnaireItem,
    questionType: string | undefined,
    sourceListUrl: string | undefined,
  ): string | undefined {
    switch (questionType) {
      case singleQuestionType:
      case categoricalSingleChoiceQuestionType:
      case multipleChoiceQuestionType:
      case symptomsQuestionType:
      case listQuestionType:
        return 'coding';
      case 'group_single':
      case groupMultipleQuestionType:
        return 'group';
      case 'boolean':
        return 'boolean';
      case 'slider':
        return item.type === QuestionnaireItemTypeEnum.DECIMAL ? 'decimal' : 'integer';
      case 'free_text':
        return 'string';
      case 'image_upload':
        return 'attachment';
      default:
        if (sourceListUrl) {
          return 'coding';
        }

        return undefined;
    }
  }


  private getExtensionString(extensions: readonly Extension[] | undefined, url: string): string | undefined {
    const extension = extensions?.find((candidate) => candidate.url === url);
    return extension?.valueCode ?? extension?.valueString ?? extension?.valueUri ?? extension?.valueMarkdown ?? undefined;
  }

  private getExtensionNumber(extensions: readonly Extension[] | undefined, url: string): number | undefined {
    const extension = extensions?.find((candidate) => candidate.url === url);
    return extension?.valueDecimal ?? extension?.valueInteger ?? undefined;
  }

  private getExtensionCodes(extensions: readonly Extension[] | undefined, url: string): readonly string[] {
    const extension = extensions?.find((candidate) => candidate.url === url);
    const codingCodes = extension?.valueCodeableConcept?.coding
      ?.map((coding) => coding.code?.trim())
      .filter((code): code is string => Boolean(code))
      ?? [];
    const text = extension?.valueCodeableConcept?.text?.trim();

    return text ? [...codingCodes, text] : codingCodes;
  }

  private getAnswerExpressionQuery(extensions: readonly Extension[] | undefined): string | undefined {
    const extension = extensions?.find((candidate) => candidate.url === answerExpressionExtensionUrl);
    const expression = extension?.valueExpression;
    if (!expression) {
      return undefined;
    }

    const language = expression.language?.trim().toLowerCase();
    if (language && language !== fhirQueryExpressionLanguage) {
      return undefined;
    }

    const candidate = expression.expression?.trim() ?? expression.reference?.trim();
    if (!candidate) {
      return undefined;
    }

    return candidate;
  }
}